import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Fuel, RefreshCw } from 'lucide-react';
import { GasInfo } from '../types';
import { apiService } from '../services/api';

const GasContainer = styled.div`
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 20px;
  box-shadow: var(--shadow-md);
  margin: 20px 0;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
`;

const GasHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const GasTitle = styled.h3`
  margin: 0;
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: color 0.3s ease;
`;

const RefreshButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const GasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const GasItem = styled.div`
  background: var(--bg-secondary);
  border-radius: 8px;
  padding: 16px;
  border-left: 4px solid var(--accent-primary);
  transition: all 0.3s ease;
`;

const GasLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-bottom: 4px;
  transition: color 0.3s ease;
`;

const GasValue = styled.div`
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  transition: color 0.3s ease;
`;

const GasUnit = styled.span`
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 400;
  transition: color 0.3s ease;
  margin-left: 4px;
`;

const ErrorMessage = styled.div`
  color: var(--error);
  font-size: 0.875rem;
  text-align: center;
  padding: 20px;
  transition: color 0.3s ease;
`;

const LoadingMessage = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
  text-align: center;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
  gap: 8px;
`;

const LastUpdated = styled.div`
  color: var(--text-muted);
  transition: color 0.3s ease;
  font-size: 0.75rem;
  text-align: right;
  margin-top: 12px;
`;

interface GasInfoPanelProps {
  rpcUrl?: string;
  network?: string; // default 'base'
}

const GasInfoPanel: React.FC<GasInfoPanelProps> = ({ rpcUrl, network = 'base' }) => {
  const [gasInfo, setGasInfo] = useState<GasInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadGasInfo = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await apiService.getGasInfo(rpcUrl || 'https://mainnet.base.org', network || 'base');
      setGasInfo(data);
      setLastUpdated(new Date());
    } catch (error: any) {
      // Логируем только в dev режиме
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to load gas info:', error);
      }
      
      // Показываем понятное сообщение об ошибке
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setError('Connection timeout. Please check your network or try again later.');
      } else {
        setError(error.response?.data?.detail || 'Failed to load gas information. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Загружаем данные при монтировании
    loadGasInfo();
    
    // Автообновление каждые 30 секунд
    const interval = setInterval(loadGasInfo, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GasContainer>
      <GasHeader>
        <GasTitle>
          <Fuel size={20} />
          ⛽ Gas Information
        </GasTitle>
        <RefreshButton onClick={loadGasInfo} disabled={isLoading}>
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </RefreshButton>
      </GasHeader>

      {isLoading && !gasInfo && (
        <LoadingMessage>
          <RefreshCw size={16} className="animate-spin" />
          Loading gas information...
        </LoadingMessage>
      )}

      {error && !gasInfo && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      {gasInfo && (
        <>
          <GasGrid>
            <GasItem>
              <GasLabel>Current Gas Price</GasLabel>
              <GasValue>
                {gasInfo.current?.gas_price_gwei?.toFixed(3) || gasInfo.gas_price_gwei?.toFixed(3) || '0.000'}
                <GasUnit>Gwei</GasUnit>
              </GasValue>
            </GasItem>

            <GasItem>
              <GasLabel>Estimated Swap Cost</GasLabel>
              <GasValue>
                {gasInfo.current?.estimated_swap_cost_eth?.toFixed(8) || gasInfo.estimated_swap_cost_eth?.toFixed(8) || '0.00000000'}
                <GasUnit>ETH</GasUnit>
              </GasValue>
            </GasItem>

            <GasItem>
              <GasLabel>Estimated Cost (USD)</GasLabel>
              <GasValue>
                ${gasInfo.current?.estimated_swap_cost_usd?.toFixed(2) || gasInfo.estimated_swap_cost_usd?.toFixed(2) || '0.00'}
                <GasUnit>USD</GasUnit>
              </GasValue>
            </GasItem>
          </GasGrid>

          <LastUpdated>
            {gasInfo.timestamp && `API timestamp: ${new Date(gasInfo.timestamp).toLocaleString()}`}
            {lastUpdated && ` • Refreshed: ${lastUpdated.toLocaleTimeString()}`}
          </LastUpdated>
        </>
      )}
    </GasContainer>
  );
};

export default GasInfoPanel;
